import React, { useState, useEffect, useRef, useCallback } from "react";
import Loading from "../Loading/Loading";
import NewsListItem from "./NewsListItem";
import styles from "./newsList.module.css";
import FilterModal from "./FilterModal";
import NoNews from "../../../components/Error/NoNews/NoNews";
import { Icons } from "../../../../code/IconHandler";
const NewsList = () => {
    const [articles, setArticles] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [usedCategories, setUsedCategories] = useState([]);

    const [selectedAuthor, setSelectedAuthor] = useState("");
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedDateFrom, setSelectedDateFrom] = useState("");
    const [selectedDateTo, setSelectedDateTo] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef(null);

    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const limit = 20; // articles per page

    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const toggleFilterModal = () => setIsFilterModalOpen(prev => !prev);

    const FilterIndicator = ({ label, value, onClear }) => (
        <div className={styles.filterIndicator}>
            <Icons.X className={styles.closeIndicator} onClick={onClear} />
            <span>{value}</span>
        </div>
    );
    useEffect(() => {
        if (!isFilterModalOpen) return;

        const fetchAuthors = async () => {
            try {
                const res = await fetch('/api/news/get_news_authors');
                const data = await res.json();
                setAuthors(data);
            } catch (err) {
                console.error("❌ Failed to load authors:", err);
            }
        };

        fetchAuthors();
    }, [isFilterModalOpen]);
    const getAllDescendants = (categoryName, allCategories) => {
        const childMap = new Map();
        allCategories.forEach(cat => {
            if (!childMap.has(cat.parent_category)) {
                childMap.set(cat.parent_category, []);
            }
            childMap.get(cat.parent_category).push(cat.name);
        });

        const result = new Set();
        const stack = [categoryName];

        while (stack.length > 0) {
            const current = stack.pop();
            if (!result.has(current)) {
                result.add(current);
                const children = childMap.get(current) || [];
                stack.push(...children);
            }
        }

        return Array.from(result);
    };

    const getExpandedSelectedCategories = useCallback((selected, allCategories) => {
        const expanded = new Set();
        selected.forEach(cat => {
            const descendants = getAllDescendants(cat, allCategories);
            descendants.forEach(d => expanded.add(d));
        });
        return Array.from(expanded);
    }, []);

    const fetchFilteredNews = async (page, reset = false) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('limit', limit);
            params.append('offset', page * limit);
            if (searchTerm) params.append('search', searchTerm);
            if (selectedAuthor) params.append('author', selectedAuthor);
            if (selectedCategories.length > 0) params.append('categories', selectedCategories.join(','));
            if (selectedDateFrom) params.append('date_from', selectedDateFrom);
            if (selectedDateTo) params.append('date_to', selectedDateTo);

            const res = await fetch(`/api/news/get_all_news?${params.toString()}`);
            if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
            const data = await res.json();

            if (reset) {
                setArticles(data.articles);
            } else {
                setArticles(prev => [...prev, ...data.articles]);
            }

            setHasMore(data.articles.length === limit);
            setError(false);
        } catch (err) {
            console.error("Error fetching filtered news:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    // Reset articles & page on filters/search change
    useEffect(() => {
        setPage(0);
        fetchFilteredNews(0, true);
    }, [searchTerm, selectedAuthor, selectedCategories, selectedDateFrom, selectedDateTo]);

    // Load more on page increment
    useEffect(() => {
        if (page === 0) return; // already loaded on filter change
        fetchFilteredNews(page, false);
    }, [page]);

    // Infinite scroll handler
    const loadMoreArticles = useCallback(() => {
        if (loading || !hasMore) return;
        setPage(prev => prev + 1);
    }, [loading, hasMore]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const distanceToBottom = scrollHeight - scrollTop - clientHeight;
            if (distanceToBottom < 100) {
                loadMoreArticles(); // this should increment page
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loadMoreArticles]);

    // Fetch categories for Abteilungen
    useEffect(() => {
        fetch("/api/categories/get_all_categories")
            .then(res => res.json())
            .then(data => setCategories(data))
            .catch(err => {
                console.error("Error fetching categories:", err);
                setCategories([]);
            });
    }, []);
    useEffect(() => {
        fetch("/api/categories/get_used_categories")
            .then(res => res.json())
            .then(data => {
                setUsedCategories(data);
                // setSelectedCategories(data.map(cat => cat.category_name)); // assuming `name` is the category label
            })
            .catch(err => {
                console.error("Error fetching used categories:", err);
                setUsedCategories([]);
            });
    }, []);

    const convertDDMMYYYYtoISO = (dateStr) => {
        if (dateStr === "") {
            return "..."
        }
        const [day, month, year] = dateStr.split(".");
        return `${year}-${month}-${day}`;
    };
    const convertYYYYMMDDtoDDMMYYYY = (dateStr) => {
        if (dateStr === "") {
            return "..."
        }
        const [year, month, day] = dateStr.split("-");
        return `${day}.${month}.${year}`;
    };


    if (error) {
        return <div className={styles.container}>
            <h1>News</h1>
            <div className={styles.containerWrapperNoNews}>
                <NoNews layout="horizontal"></NoNews>
            </div>
        </div>;
    }

    return (
        <div className={styles.container}>
            <h1>News</h1>
            <div className={styles.containerWrapper}>
                <div className={styles.contentWrapper}>
                    <div className={styles.filterContainer}>
                        <input
                            type="text"
                            placeholder="Durchsuche die Nachrichten..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                        />
                        <div tabIndex={0}
                            className={styles.filter} onClick={toggleFilterModal}>
                            <p>Filtern nach ...</p>
                        </div>
                    </div>
                    <div className={styles.activeFilters}>
                        {selectedAuthor && (
                            <FilterIndicator
                                label="Autor:in"
                                value={authors.find(a => a.author_id === parseInt(selectedAuthor))?.author_name || "Unbekannt"}
                                onClear={() => setSelectedAuthor("")}
                            />
                        )}
                        {selectedCategories.map((item, index) => (
                            <FilterIndicator
                                key={index}
                                label="Kategorie"
                                value={item}
                                onClear={() => {
                                    setSelectedCategories(prev =>
                                        prev.filter((_, i) => i !== index)
                                    );
                                }}
                            />
                        ))}
                        {(selectedDateFrom || selectedDateTo) && (
                            <FilterIndicator
                                label="Zeitraum"
                                value={`von ${convertYYYYMMDDtoDDMMYYYY(selectedDateFrom) || "..."} bis ${convertYYYYMMDDtoDDMMYYYY(selectedDateTo) || "..."}`}
                                onClear={() => {
                                    setSelectedDateFrom("");
                                    setSelectedDateTo("");
                                }}
                            />
                        )}
                    </div>

                    <div ref={containerRef} className={styles.content}>
                        {articles.length === 0 ? (
                            <div className={styles.nothingFound}>
                                <p>Keine Nachrichten hinsichtlich der Suchparameter gefunden</p>
                            </div>
                        ) : (
                            <div>
                                {articles.map((article) => (
                                    <NewsListItem key={article.article_id} article={article} />
                                ))}
                                {loading && <Loading />}
                            </div>
                        )}
                    </div>
                </div>

                {isFilterModalOpen && (
                    <FilterModal
                        authors={authors}
                        categories={categories}
                        usedCategories={usedCategories}
                        selectedAuthor={selectedAuthor}
                        setSelectedAuthor={setSelectedAuthor}
                        selectedCategories={selectedCategories}
                        setSelectedCategories={setSelectedCategories}
                        selectedDateFrom={selectedDateFrom}
                        setSelectedDateFrom={setSelectedDateFrom}
                        selectedDateTo={selectedDateTo}
                        setSelectedDateTo={setSelectedDateTo}
                        onClose={toggleFilterModal}
                    />
                )}
            </div>
        </div>
    );
};

export default NewsList;
